import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/app/api";
import { preOrderKeys } from "./keys";
import type { UUID } from "crypto";

/**
 * Surgically update a single pre-order's flows in ALL cached queries.
 * Iterates every ["pre-orders", ...] cache entry once and applies
 * flowUpdater only to the matching preOrderId.
 */
function updatePreOrderFlowsInAllCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  preOrderId: string,
  flowUpdater: (flows: any[]) => any[],
) {
  const updatePo = (po: any) => {
    if (po?.id !== preOrderId) return po;
    return { ...po, flows: flowUpdater(po.flows || []) };
  };

  // 1. Detail cache for this specific pre-order
  const detailKey = preOrderKeys.detail(preOrderId as UUID);
  const detailData = queryClient.getQueryData(detailKey) as any;
  if (detailData?.id === preOrderId) {
    queryClient.setQueryData(detailKey, updatePo(detailData));
  }

  // 2. Recap queries (array of pre-orders) — only the active date's cache
  queryClient
    .getQueryCache()
    .findAll({ queryKey: [...preOrderKeys.all, "recap"] })
    .forEach((query) => {
      const data = query.state.data;
      if (!Array.isArray(data)) return;
      if (!data.some((po: any) => po?.id === preOrderId)) return;
      queryClient.setQueryData(query.queryKey, data.map(updatePo));
    });

  // 3. Paginated queries (list, chat, sms-chat, email) — skip if no match
  for (const suffix of ["list", "chat", "sms-chat", "email"] as const) {
    queryClient
      .getQueryCache()
      .findAll({ queryKey: [...preOrderKeys.all, suffix] })
      .forEach((query) => {
        const data = query.state.data as any;
        if (!data?.items || !Array.isArray(data.items)) return;
        if (!data.items.some((po: any) => po?.id === preOrderId)) return;
        queryClient.setQueryData(query.queryKey, {
          ...data,
          items: data.items.map(updatePo),
        });
      });
  }
}

/**
 * Snapshot a single pre-order's state from all caches for rollback.
 * Returns a list of { queryKey, data } pairs.
 */
function snapshotCaches(queryClient: ReturnType<typeof useQueryClient>) {
  return queryClient
    .getQueryCache()
    .findAll({ queryKey: preOrderKeys.all })
    .map((query) => ({
      queryKey: query.queryKey,
      data: query.state.data,
    }));
}

function rollbackCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  snapshots: { queryKey: readonly unknown[]; data: unknown }[],
) {
  for (const { queryKey, data } of snapshots) {
    queryClient.setQueryData(queryKey, data);
  }
}

/**
 * Hook to create a pre-order flow
 */
export function useCreatePreOrderFlow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ preOrderId, flow }: { preOrderId: string; flow: any }) => {
      return api.preOrders.createPreOrderFlow(preOrderId, flow);
    },

    onMutate: ({ preOrderId, flow }) => {
      const tempId = flow.id || `temp-${Date.now()}-${Math.random()}`;
      const now = new Date().toISOString();
      // Same complete flow sent to backend is used for optimistic cache
      const optimisticFlow = {
        ...flow,
        id: tempId,
        created_at: now,
        updated_at: now,
      };

      // Fire-and-forget: abort signal is set synchronously, no need to await
      // No cancelQueries — we set optimistic data directly.
      // Stale queries refetch in the background without losing current data.
      const snapshots = snapshotCaches(queryClient);

      // Add to standalone flows cache
      const previousFlows =
        (queryClient.getQueryData(preOrderKeys.flows(preOrderId)) as any[]) ||
        [];
      queryClient.setQueryData(preOrderKeys.flows(preOrderId), [
        ...previousFlows,
        optimisticFlow,
      ]);

      // Add to this pre-order in every other cache
      updatePreOrderFlowsInAllCaches(queryClient, preOrderId, (flows) => [
        ...flows,
        optimisticFlow,
      ]);

      return { snapshots, preOrderId, tempId };
    },

    onError: (_error, _variables, context) => {
      if (context?.snapshots) rollbackCaches(queryClient, context.snapshots);
    },

    onSuccess: (newFlow, variables, context) => {
      if (context?.tempId) {
        // Replace temp flow with full server response in all caches
        const replaceTemp = (f: any) =>
          f.id === context.tempId ? { ...f, ...newFlow } : f;

        // Replace in standalone flows cache
        const currentFlows =
          (queryClient.getQueryData(
            preOrderKeys.flows(variables.preOrderId),
          ) as any[]) || [];
        queryClient.setQueryData(
          preOrderKeys.flows(variables.preOrderId),
          currentFlows.map(replaceTemp),
        );

        // Replace in all other caches
        updatePreOrderFlowsInAllCaches(
          queryClient,
          variables.preOrderId,
          (flows) => flows.map(replaceTemp),
        );
      }

      // Only invalidate standalone flows + detail, skip recap to avoid full re-render
      queryClient.invalidateQueries({
        queryKey: preOrderKeys.flows(variables.preOrderId),
      });
      queryClient.invalidateQueries({
        queryKey: preOrderKeys.detail(variables.preOrderId as UUID),
      });
    },
  });
}

/**
 * Hook to update a pre-order flow
 */
export function useUpdatePreOrderFlow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      preOrderId,
      flowId,
      data,
    }: {
      preOrderId: string;
      flowId: string;
      data: any;
    }) => api.preOrders.updatePreOrderFlow(preOrderId, flowId, data),

    onMutate: ({ preOrderId, flowId, data }) => {
      const snapshots = snapshotCaches(queryClient);

      const updateFlow = (f: any) => (f.id === flowId ? { ...f, ...data } : f);

      // Update standalone flows cache
      const previousFlows = queryClient.getQueryData(
        preOrderKeys.flows(preOrderId),
      );
      if (Array.isArray(previousFlows)) {
        queryClient.setQueryData(
          preOrderKeys.flows(preOrderId),
          previousFlows.map(updateFlow),
        );
      }

      // Update in all other caches
      updatePreOrderFlowsInAllCaches(queryClient, preOrderId, (flows) =>
        flows.map(updateFlow),
      );

      return { snapshots, preOrderId };
    },

    onError: (_error, _variables, context) => {
      if (context?.snapshots) rollbackCaches(queryClient, context.snapshots);
    },

    onSuccess: (serverFlow, variables) => {
      // Merge server response into all caches (replaces optimistic data with real server data)
      const replaceFlow = (f: any) =>
        f.id === variables.flowId ? { ...f, ...serverFlow } : f;

      // Update standalone flows cache
      const currentFlows = queryClient.getQueryData(
        preOrderKeys.flows(variables.preOrderId),
      );
      if (Array.isArray(currentFlows)) {
        queryClient.setQueryData(
          preOrderKeys.flows(variables.preOrderId),
          currentFlows.map(replaceFlow),
        );
      }

      // Update in all other caches (recap, list, chat, etc.)
      updatePreOrderFlowsInAllCaches(
        queryClient,
        variables.preOrderId,
        (flows) => flows.map(replaceFlow),
      );
    },
  });
}

/**
 * Hook to delete a pre-order flow
 */
export function useDeletePreOrderFlow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      preOrderId,
      flowId,
    }: {
      preOrderId: string;
      flowId: string;
    }) => api.preOrders.deletePreOrderFlow(preOrderId, flowId),

    onMutate: ({ preOrderId, flowId }) => {
      // No cancelQueries — we set optimistic data directly.
      // Stale queries refetch in the background without losing current data.
      const snapshots = snapshotCaches(queryClient);

      // Remove from standalone flows cache
      const previousFlows = queryClient.getQueryData(
        preOrderKeys.flows(preOrderId),
      );
      if (Array.isArray(previousFlows)) {
        queryClient.setQueryData(
          preOrderKeys.flows(preOrderId),
          previousFlows.filter((f: any) => f.id !== flowId),
        );
      }

      // Remove from all other caches
      updatePreOrderFlowsInAllCaches(queryClient, preOrderId, (flows) =>
        flows.filter((f) => f.id !== flowId),
      );

      return { snapshots, preOrderId };
    },

    onError: (_error, _variables, context) => {
      if (context?.snapshots) rollbackCaches(queryClient, context.snapshots);
    },

    onSettled: (_data, _error, variables) => {
      // Only invalidate specific pre-order caches, not all recap queries
      queryClient.invalidateQueries({
        queryKey: preOrderKeys.flows(variables.preOrderId),
      });
      queryClient.invalidateQueries({
        queryKey: preOrderKeys.detail(variables.preOrderId as UUID),
      });
    },
  });
}
